import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { adminApi } from "../api/admin";
import { formatNumber } from "../lib/format";
import { Loading } from "./Loading";

/**
 * Tells an admin how many customers still have nobody assigned, and hands them the
 * list filtered to exactly those rows.
 *
 * <p>Deliberately a dashboard card and not a notification: `notification` rows are
 * per-user, event-shaped and carry no dedup key, so a standing condition like this
 * one would insert a fresh unread row on every run and be ignored within a week.
 * A card reads the number live and is silent once the number reaches zero.
 */
export function UnassignedCompaniesWidget() {
  const { t, i18n } = useTranslation();
  const [count, setCount] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    void adminApi
      .unassignedCompanyCount()
      .then((r) => setCount(r.count))
      .catch(() => setFailed(true));
  }, []);

  // A failed count says nothing useful, and a zero is good news that needs no card.
  if (failed || count === 0) return null;

  return (
    <section style={card}>
      <header style={head}>
        <strong>{t("widgets.unassignedCompanies.title")}</strong>
      </header>
      {count === null ? (
        <Loading />
      ) : (
        <>
          <div style={number}>{formatNumber(count, 0, i18n.language)}</div>
          <p style={body}>{t("widgets.unassignedCompanies.body", { count })}</p>
          <Link to="/admin/companies?salesUser=none" style={action}>
            {t("widgets.unassignedCompanies.action")}
          </Link>
        </>
      )}
    </section>
  );
}

const card: React.CSSProperties = {
  background: "#fff", padding: 16, borderRadius: 10, boxShadow: "0 1px 2px rgba(0,0,0,.04)",
  display: "flex", flexDirection: "column", gap: 8,
};
const head: React.CSSProperties = { color: "#1f2937", fontSize: 14 };
const number: React.CSSProperties = { fontSize: 28, fontWeight: 700, color: "#b45309", lineHeight: 1.1 };
const body: React.CSSProperties = { color: "#6b7280", fontSize: 12, margin: 0 };
const action: React.CSSProperties = { color: "#0b3a6f", fontSize: 12, textDecoration: "none" };
